import { UM } from '../_components/tokens';
import type { Layer, Step, StepId } from './types';

// ─── Primitives visuelles ────────────────────────────────────────────────────

function EyeToggle({ visible, onClick }: { visible: boolean; onClick: () => void }) {
  const c = visible ? UM.accent : '#b0c4d4';
  return (
    <span
      onClick={onClick}
      title={visible ? 'Masquer la couche' : 'Afficher la couche'}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: '50%',
        background: visible ? UM.accentLight : '#f1f5f9',
        cursor: 'pointer', flexShrink: 0,
      }}
    >
      <svg width="13" height="9" viewBox="0 0 13 9">
        <ellipse cx="6.5" cy="4.5" rx="6" ry="4" stroke={c} strokeWidth="1.3" fill="none" />
        <circle cx="6.5" cy="4.5" r="2" fill={c} />
      </svg>
    </span>
  );
}

function SourceBadge({ source }: { source: 'engie' | 'open' }) {
  const isEngie = source === 'engie';
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, letterSpacing: 0.3,
      background: isEngie ? UM.badgeBlue : '#f1f5f9',
      color: isEngie ? UM.badgeBlueText : UM.textMuted,
      padding: '2px 6px', borderRadius: 3,
      textTransform: 'uppercase', flexShrink: 0,
    }}>
      {isEngie ? 'ENGIE' : 'Open Data'}
    </span>
  );
}

// ─── Ligne de couche ─────────────────────────────────────────────────────────

function LayerRow({ layer, visible, onToggleVisible }: { layer: Layer; visible: boolean; onToggleVisible: () => void }) {
  return (
    <div
      title={layer.hint}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px 6px 28px' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = UM.bgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <EyeToggle visible={visible} onClick={onToggleVisible} />
      <span style={{
        flex: 1, fontSize: 13,
        color: visible ? UM.text : UM.textMuted,
        fontWeight: visible ? 500 : 400,
      }}>
        {layer.label}
      </span>
      <SourceBadge source={layer.source} />
    </div>
  );
}

// ─── En-tête d'étape ─────────────────────────────────────────────────────────

interface StepHeaderProps {
  step: Step;
  open: boolean;
  activeInStep: number;
  totalInStep: number;
  onToggle: () => void;
}

function StepHeader({ step, open, activeInStep, totalInStep, onToggle }: StepHeaderProps) {
  return (
    <button
      onClick={onToggle}
      style={{
        width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 12px',
        background: open ? UM.accentLight : 'transparent',
        borderLeft: `3px solid ${open ? UM.accent : 'transparent'}`,
      }}
    >
      <span style={{
        width: 24, height: 24, borderRadius: '50%',
        background: step.primary ? UM.accent : UM.bgHeader,
        color: step.primary ? '#fff' : UM.text,
        border: step.primary ? 'none' : `1px solid ${UM.border}`,
        fontSize: 12, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {step.number}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: UM.text }}>
          {step.title}
        </span>
        <span style={{ display: 'block', fontSize: 11, color: UM.textMuted, marginTop: 1 }}>
          {step.subtitle}
        </span>
      </span>
      {activeInStep > 0 && (
        <span style={{
          fontSize: 11, fontWeight: 600, color: UM.accent,
          background: '#fff', padding: '2px 6px', borderRadius: 10,
          border: `1px solid ${UM.accent}`,
        }}>
          {activeInStep}/{totalInStep}
        </span>
      )}
      <span style={{ color: UM.textMuted, fontSize: 10, flexShrink: 0 }}>
        {open ? '▲' : '▼'}
      </span>
    </button>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export interface JourneyPanelProps {
  steps: Step[];
  layersByStep: Map<StepId, Layer[]>;
  visible: Set<string>;
  openStep: StepId | null;
  search: string;
  activeCount: number;
  onSearch: (s: string) => void;
  onToggleVisible: (id: string) => void;
  onToggleStep: (id: StepId) => void;
  onClearAll: () => void;
  onClose: () => void;
}

export function JourneyPanel(props: JourneyPanelProps) {
  return (
    <div style={{
      width: 340, minWidth: 340, background: UM.bg,
      borderRight: `1px solid ${UM.border}`,
      display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'hidden', zIndex: 15,
    }}>
      {/* Header */}
      <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${UM.border}`, background: UM.bgHeader, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: UM.text }}>Couches de données</span>
          <button
            onClick={props.onClose}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: UM.textMuted, fontSize: 18, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={props.search}
            onChange={(e) => props.onSearch(e.target.value)}
            placeholder="Rechercher une couche…"
            style={{
              width: '100%', padding: '7px 10px 7px 30px', borderRadius: 6,
              border: `1px solid ${UM.border}`, fontSize: 13, background: UM.bg, color: UM.text,
              boxSizing: 'border-box',
            }}
          />
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: UM.textDim, fontSize: 13 }}>⌕</span>
        </div>

      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '14px 16px 8px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: UM.textMuted }}>
            Parcours d'étude
          </div>
          <div style={{ fontSize: 11, color: UM.textDim, marginTop: 2 }}>
            Cliquez sur une étape pour voir les couches associées
          </div>
        </div>

        {props.steps.map((step) => {
          const layers = props.layersByStep.get(step.id) ?? [];
          const active = layers.filter((l) => props.visible.has(l.id)).length;
          const open = props.openStep === step.id;
          return (
            <div key={step.id} style={{ borderTop: `1px solid ${UM.border}` }}>
              <StepHeader
                step={step}
                open={open}
                activeInStep={active}
                totalInStep={layers.length}
                onToggle={() => props.onToggleStep(step.id)}
              />
              {open && (
                <div style={{ padding: '4px 0 10px' }}>
                  {layers.length === 0 ? (
                    <div style={{ padding: '8px 28px', fontSize: 12, color: UM.textDim, fontStyle: 'italic' }}>
                      Aucune couche ne correspond
                    </div>
                  ) : (
                    layers.map((layer) => (
                      <LayerRow
                        key={layer.id}
                        layer={layer}
                        visible={props.visible.has(layer.id)}
                        onToggleVisible={() => props.onToggleVisible(layer.id)}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer — état des couches actives */}
      <div style={{
        padding: '10px 16px',
        borderTop: `1px solid ${UM.border}`,
        background: UM.bgHeader,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 12,
      }}>
        <span style={{ color: UM.textMuted }}>
          {props.activeCount === 0 ? 'Aucune couche activée' : (
            <><strong style={{ color: UM.accent }}>{props.activeCount}</strong> couche{props.activeCount > 1 ? 's' : ''} activée{props.activeCount > 1 ? 's' : ''}</>
          )}
        </span>
        {props.activeCount > 0 && (
          <button
            onClick={props.onClearAll}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: UM.textMuted, fontSize: 12, textDecoration: 'underline' }}
          >
            Tout masquer
          </button>
        )}
      </div>
    </div>
  );
}
